import { useCallback, useEffect, useMemo, useState } from 'react';
import { actualizarClasificacionAlumno } from '../services/clasificacionAlumnos';
import { cuentaEnMetricas, esAlumnoOculto } from '../utils/clasificacionAlumnos';

export const esAlumnoOcultable = (usuario) => Boolean(
  usuario?.id && !usuario.teacher && !usuario.isMaster && usuario.isSuperAdmin !== true
);

// La clasificación es compartida; sólo el dueño puede cambiarla o revisar ocultos.
export const useAlumnosOcultos = (currentUser) => {
  const uid = currentUser?.uid || currentUser?.id;
  const puedeOcultar = currentUser?.isSuperAdmin === true && Boolean(uid);
  const preferencias = currentUser?.preferenciasVista?.alumnosOcultos;
  const idsOcultos = useMemo(() => new Set(
    puedeOcultar && Array.isArray(preferencias)
      ? preferencias.filter((id) => typeof id === 'string')
      : []
  ), [puedeOcultar, preferencias]);
  const [vista, setVista] = useState({ uid, mostrar: false });
  const [operacion, setOperacion] = useState(null);
  const [fallo, setFallo] = useState(null);
  const [cambios, setCambios] = useState({ uid, alumnos: {} });
  const sincronizar = useCallback(() => setCambios({ uid, alumnos: {} }), [uid]);
  const incorporar = useCallback((alumnos) => setCambios(prev => ({
    uid, alumnos: { ...(prev.uid === uid ? prev.alumnos : {}),
      ...Object.fromEntries(alumnos.map(alumno => [alumno.id, alumno])) },
  })), [uid]);
  const perfilActual = useCallback(alumno => ({ ...alumno,
    ...(cambios.uid === uid ? cambios.alumnos[alumno.id] : {}),
  }), [cambios, uid]);

  useEffect(() => {
    if (!puedeOcultar || !idsOcultos.size) return undefined;
    actualizarClasificacionAlumno({ migrarOcultos: true }).then(alumnos => {
      incorporar(alumnos);
    }).catch(() => {
      setFallo({ uid, mensaje: 'No se pudieron compartir los alumnos ocultos anteriores. Recarga para reintentar.' });
    });
    return undefined;
  }, [puedeOcultar, idsOcultos, incorporar, uid]);
  const mostrarOcultos = puedeOcultar && vista.uid === uid && vista.mostrar;
  const actualizandoId = operacion?.uid === uid ? operacion.alumnoId : '';
  const error = fallo?.uid === uid ? fallo.mensaje : '';

  const estaOculto = useCallback((alumno) => (
    esAlumnoOculto(perfilActual(alumno))
      || (puedeOcultar && esAlumnoOcultable(alumno) && idsOcultos.has(alumno.id)
        && !(cambios.uid === uid && cambios.alumnos[alumno.id]))
  ), [puedeOcultar, idsOcultos, perfilActual, cambios, uid]);
  const cuentaEnEstadisticas = useCallback(alumno => (
    cuentaEnMetricas(perfilActual(alumno)) && !estaOculto(alumno)
  ), [perfilActual, estaOculto]);
  const esVisible = useCallback((alumno) => (
    mostrarOcultos ? estaOculto(alumno) : !estaOculto(alumno)
  ), [mostrarOcultos, estaOculto]);
  const setMostrarOcultos = useCallback((mostrar) => {
    setVista({ uid, mostrar: Boolean(mostrar) });
  }, [uid]);

  const cambiarVisibilidad = useCallback(async (alumno) => {
    if (!puedeOcultar || !esAlumnoOcultable(alumno) || actualizandoId) return;
    setOperacion({ uid, alumnoId: alumno.id });
    setFallo(null);
    try {
      incorporar(await actualizarClasificacionAlumno({ uid: alumno.id,
        ocultoEnListados: !estaOculto(alumno),
        ...(estaOculto(alumno) ? { usuarioPrueba: false } : {}),
      }));
    } catch (error) {
      console.error('No se pudo guardar la visibilidad del alumno:', error);
      setFallo({ uid, mensaje: 'No se pudo guardar la preferencia. Revisa tu conexión y vuelve a intentarlo.' });
    } finally {
      setOperacion(null);
    }
  }, [puedeOcultar, uid, actualizandoId, estaOculto, incorporar]);

  return { puedeOcultar, mostrarOcultos, setMostrarOcultos, estaOculto, esVisible, cambiarVisibilidad, actualizandoId, error,
    cuentaEnEstadisticas, perfilActual, sincronizar };
};
